import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Logo from '../../components/Logo';

/**
 * AdminLogoCodeLoader (refined)
 *
 * Minimal branded boot:
 *   - subtle code-glyph columns (kept)
 *   - faint dot grid (kept)
 *   - logo emerges from the code (no sweep, no beams)
 *   - scrambling 'THE RELATIVE GALLERY' wordmark
 *   - small status dot + label
 *
 * Removed: horizontal & vertical scan beams, cyan sweep across the logo,
 * pulsing border around the logo. The structure stays componentized so
 * the <Logo/> can be swapped for a final asset later.
 */
const CODE_CHARS = '01{}[]</>#$%*&+=|~!@?:;.,'.split('');
const WORDS = ['THE', 'RELATIVE', 'GALLERY'];

function randChar() {
  return CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
}

function ScrambledWord({ target, delay = 0 }) {
  const [text, setText] = useState(() =>
    target.split('').map(() => randChar()).join(''),
  );

  useEffect(() => {
    const total = target.length;
    const duration = 700;
    const start = performance.now() + delay;
    let raf;

    const tick = (t) => {
      const elapsed = Math.max(0, t - start);
      const progress = Math.min(1, elapsed / duration);
      const resolved = Math.floor(progress * total);
      let next = '';
      for (let i = 0; i < total; i++) {
        next += i < resolved ? target[i] : randChar();
      }
      setText(next);
      if (progress < 1) raf = requestAnimationFrame(tick);
      else setText(target);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, delay]);

  return (
    <span className="font-outfit font-black tracking-tight text-white">
      {text}
    </span>
  );
}

function CodeColumn({ x, seed }) {
  const lines = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 22; i++) {
      arr.push({
        char: randChar(),
        opacity: 0.06 + Math.random() * 0.22,
        delay: (seed + i) * 0.05,
      });
    }
    return arr;
  }, [seed]);

  return (
    <div
      className="absolute top-0 bottom-0 pointer-events-none select-none font-mono text-[11px]"
      style={{ left: `${x}%`, width: 14 }}
      aria-hidden
    >
      {lines.map((l, i) => (
        <motion.span
          key={i}
          className="block text-center text-[#9aa]"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, l.opacity, l.opacity * 0.5, l.opacity] }}
          transition={{ duration: 1.6, delay: l.delay, repeat: Infinity, repeatType: 'mirror' }}
          style={{ color: i % 9 === 0 ? '#90E0EF' : '#cccccc' }}
        >
          {l.char}
        </motion.span>
      ))}
    </div>
  );
}

export default function AdminLogoCodeLoader({
  label = 'INITIALIZING CURATOR SYSTEM',
  onDone,
  minDuration = 1600,
}) {
  const [phase, setPhase] = useState('boot');

  useEffect(() => {
    const ready = setTimeout(() => setPhase('ready'), minDuration - 250);
    const done = setTimeout(() => onDone && onDone(), minDuration);
    return () => {
      clearTimeout(ready);
      clearTimeout(done);
    };
  }, [minDuration, onDone]);

  // Distribute columns across the canvas
  const columns = useMemo(() => [4, 10, 16, 22, 28, 72, 78, 84, 90, 96], []);

  return (
    <motion.div
      data-testid="admin-logo-loader"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="relative w-full min-h-[420px] bg-[#050505] overflow-hidden flex items-center justify-center"
    >
      {/* Subtle dot grid (no beams) */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.45) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      {/* Code columns — gentle flicker, no sweeping beams */}
      {columns.map((x, i) => (
        <CodeColumn key={x} x={x} seed={i} />
      ))}

      {/* Center content — logo emerges from the code */}
      <div className="relative z-10 flex flex-col items-center gap-6 px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.96, filter: 'blur(8px)' }}
          animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
          transition={{ duration: 1.0, ease: 'easeOut' }}
          style={{ color: '#ffffff' }}
        >
          <Logo size={120} />
        </motion.div>

        {/* Wordmark — scramble-resolves */}
        <div className="flex items-baseline gap-2 text-sm sm:text-base">
          {WORDS.map((w, i) => (
            <ScrambledWord key={w} target={w} delay={180 * i + 200} />
          ))}
        </div>

        {/* Status line */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex items-center gap-3"
        >
          <motion.span
            className="inline-block w-1.5 h-1.5 rounded-full"
            animate={{
              opacity: [0.35, 1, 0.35],
              backgroundColor: phase === 'ready' ? '#90E0EF' : '#ffffff',
            }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
          <motion.p
            key={phase}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#90E0EF]"
          >
            {phase === 'ready' ? 'SYSTEM · READY' : label}
          </motion.p>
        </motion.div>
      </div>

      {/* Quiet corner ticks */}
      <Corner pos="top-left" />
      <Corner pos="top-right" />
      <Corner pos="bottom-left" />
      <Corner pos="bottom-right" />
    </motion.div>
  );
}

function Corner({ pos }) {
  const styles = {
    'top-left': 'top-3 left-3 border-l border-t',
    'top-right': 'top-3 right-3 border-r border-t',
    'bottom-left': 'bottom-3 left-3 border-l border-b',
    'bottom-right': 'bottom-3 right-3 border-r border-b',
  };
  return <span aria-hidden className={`absolute w-3 h-3 ${styles[pos]} border-[#90E0EF]/25`} />;
}
